import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Trash2, Undo2, RotateCcw, Edit } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { Match, Team, ScoreEntry, Wicket, BattingStats, BowlingStats, Player } from '@/types/cricket';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

interface ScoreHistoryManagerProps {
  scoreHistory: ScoreEntry[];
  onDeleteEntry: (entryId: string) => Promise<void>;
  onUndoToIndex: (targetIndex: number) => Promise<void>;
  onRestoreEntry: (entry: ScoreEntry) => void;
  match: Match;
  teams: Team[];
  players: Player[];
  onEditEntry: (entry: ScoreEntry) => void;
  onSaveEdit: () => Promise<void>;
  onCancelEdit: () => void;
  editingEntry: ScoreEntry | null;
  editingEntryId: string | null;
  isLoading: boolean;
  onBallClick: (ball: ScoreEntry) => void;
}

export const ScoreHistoryManager: React.FC<ScoreHistoryManagerProps> = ({
  scoreHistory,
  onDeleteEntry,
  onUndoToIndex,
  onRestoreEntry,
  match,
  teams,
  players,
  onEditEntry,
  onSaveEdit,
  onCancelEdit,
  editingEntry,
  editingEntryId,
  isLoading,
  onBallClick
}) => {
  const [deletedEntries, setDeletedEntries] = useState<ScoreEntry[]>([]);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<ScoreEntry>>({});

  const handleDeleteSingle = async (entryId: string) => {
    try {
      const entryToDelete = scoreHistory.find(e => e.id === entryId);
      if (entryToDelete) {
        setDeletedEntries(prev => [...prev, entryToDelete]);
      }
      
      await onDeleteEntry(entryId);
      setConfirmDelete(null);
      
      toast({
        title: "Score Deleted",
        description: "Score entry has been removed. You can restore it from the deleted items section.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete score entry",
        variant: "destructive",
      });
    }
  };

  const handleRestoreEntry = (entry: ScoreEntry) => {
    onRestoreEntry(entry);
    setDeletedEntries(prev => prev.filter(e => e.id !== entry.id));
    
    toast({
      title: "Score Restored",
      description: "Score entry has been restored successfully.",
    });
  };

  const handleEditEntry = (entry: ScoreEntry) => {
    onEditEntry(entry);
    setEditForm({
      runs: entry.runs,
      ballRuns: entry.ballRuns,
      batsmanRuns: entry.batsmanRuns,
      bowlerRuns: entry.bowlerRuns,
      extras: entry.extras,
      extraType: entry.extraType,
      ballCount: entry.ballCount,
      description: entry.description,
      ballDirection: entry.ballDirection,
      ballAngleDeg: entry.ballAngleDeg,
      ballDistanceM: entry.ballDistanceM,
      fieldingPosition: entry.fieldingPosition,
      isFreeHit: entry.isFreeHit
    });
  };

  const handleSaveEdit = async () => {
    if (!editingEntryId) return;
    
    try {
      await onSaveEdit();
      setEditForm({});
      
      toast({
        title: "Entry Updated",
        description: "Score entry has been updated successfully.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update score entry",
        variant: "destructive",
      });
    }
  };

  const getTeamName = (teamId: string) => {
    const team = teams.find(t => t.id === teamId);
    return team?.name || 'Unknown Team';
  };

  const getInningsDisplay = (entry: ScoreEntry) => {
    if (entry.battingTeamId) {
      const teamName = getTeamName(entry.battingTeamId);
      return `${teamName} Innings`;
    }
    return 'Innings';
  };

  const groupedByInnings = scoreHistory.reduce((acc, entry) => {
    const inningsKey = entry.battingTeamId || 'unknown';
    if (!acc[inningsKey]) {
      acc[inningsKey] = [];
    }
    acc[inningsKey].push(entry);
    return acc;
  }, {} as Record<string, ScoreEntry[]>);

  return (
        <div className="space-y-6 max-h-96 overflow-y-auto">
          {/* Current Score History by Innings */}
          <div>
            <h3 className="font-semibold mb-3">Current Score History</h3>
            {Object.keys(groupedByInnings).length === 0 ? (
              <p className="text-center text-gray-500 py-4">No score history yet</p>
            ) : (
              <div className="space-y-4">
                {Object.entries(groupedByInnings).map(([inningsKey, entries]) => (
                  <div key={inningsKey} className="border rounded-lg p-3">
                    <h4 className="font-medium text-blue-600 mb-2">
                      {getInningsDisplay(entries[0])}
                    </h4>
                    <div className="space-y-2">
                      {entries.map((entry, index) => (
                        <Card key={entry.id} className="shadow-sm">
                          <CardHeader className="p-3 border-b flex flex-row items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="font-bold">Ball {entry.ballNumber}</span>
                              <Badge variant="outline" className="bg-gray-100 text-gray-700">
                                Over {entry.overNumber}.{entry.ballNumber}
                              </Badge>
                              {entry.isFreeHit && (
                                <Badge className="bg-orange-500 text-white">Free Hit</Badge>
                              )}
                            </div>
                            <div className="flex gap-2">
                              <Button variant="outline" size="icon" onClick={() => handleEditEntry(entry)} disabled={isLoading}>
                                <Edit className="h-4 w-4" />
                                </Button>
                              <Button variant="destructive" size="icon" onClick={() => setConfirmDelete(entry.id)} disabled={isLoading}>
                                <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                          </CardHeader>
                          <CardContent className="p-3 text-sm space-y-1">
                            <p className={`${entry.wicket ? 'text-red-600 font-semibold' : ''}`}>{entry.description}</p>
                            <p className="text-gray-700">Team Runs: {entry.runs} | Ball Runs: {entry.ballRuns}</p>
                            <p className="text-gray-700">Striker: {players.find(p => p.id === entry.strikerId)?.name || 'Unknown'}</p>
                            <p className="text-gray-700">Non-Striker: {players.find(p => p.id === entry.nonStrikerId)?.name || 'Unknown'}</p>
                            <p className="text-gray-700">Bowler: {players.find(p => p.id === entry.bowlerId)?.name || 'Unknown'}</p>
                            {entry.wicket && entry.wicket.fielderId && (
                              <p className="text-red-600">Wicket: {entry.wicket.wicketType} - {players.find(p => p.id === entry.wicket?.fielderId)?.name || 'Unknown'}</p>
                            )}
                            <p className="text-gray-500 text-xs">Time: {new Date(entry.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</p>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Deleted Entries Section */}
          {deletedEntries.length > 0 && (
            <div>
              <h3 className="font-semibold mb-3">Deleted Entries</h3>
              <div className="space-y-4">
                {deletedEntries.map((entry) => (
                  <div key={entry.id} className="flex items-center justify-between p-2 border rounded bg-gray-50">
                    <div className="flex-1">
                      <div className="font-medium">{entry.description}</div>
                      <div className="text-sm text-gray-500">
                        Over {entry.overNumber}.{entry.ballNumber} - {new Date(entry.timestamp).toLocaleTimeString()}
                      </div>
                    </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleRestoreEntry(entry)}
                        title="Restore this entry"
                      >
                        <RotateCcw className="w-4 h-4" />
                      </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Confirmation Dialog for Delete */}
          <Dialog open={confirmDelete !== null} onOpenChange={() => setConfirmDelete(null)}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Confirm Delete</DialogTitle>
              </DialogHeader>
              <Alert variant="destructive">
            <AlertDescription>
                  Are you sure you want to delete this score entry? This action cannot be undone.
                </AlertDescription>
              </Alert>
              <div className="flex justify-end gap-2 mt-4">
                <Button variant="outline" onClick={() => setConfirmDelete(null)}>Cancel</Button>
                <Button variant="destructive" onClick={() => confirmDelete && handleDeleteSingle(confirmDelete)} disabled={isLoading}>Delete</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
  );
};
